'use client';

import { useState, useCallback, useRef, useEffect } from 'react';
import { Icon } from './Icon';

export interface VBCShareButtonProps {
  vendorId: string | number;
  vendorName: string;
  /** The canonical share URL. App provides this (e.g., full URL with domain). */
  shareUrl: string;
  /** Called when the share action fires. App handles tracking. */
  onTrackShare?: (vendorId: string | number) => void;
}

/**
 * Share button for the VBC detail page.
 * Tries navigator.share, then clipboard copy, then fallback input.
 * Tracking and URL construction are delegated to the app via props.
 */
export function VBCShareButton({
  vendorId,
  vendorName,
  shareUrl,
  onTrackShare,
}: VBCShareButtonProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu on outside click
  useEffect(() => {
    if (!showMenu) return;

    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  const handleShare = useCallback(async () => {
    // Track the share
    onTrackShare?.(vendorId);

    // Try native share API first (mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: vendorName,
          url: shareUrl,
        });
        return;
      } catch {
        // User cancelled or not supported -- fall through to copy
      }
    }

    // Copy to clipboard
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: show menu with URL
      setShowMenu(true);
    }
  }, [vendorId, vendorName, shareUrl, onTrackShare]);

  const handleCopyFromMenu = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        setShowMenu(false);
      }, 1500);
    } catch {
      // Silent fail
    }
  }, [shareUrl]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={handleShare}
        className="inline-flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-all duration-150"
        style={{
          borderColor: 'var(--brand-card-border)',
          color: 'var(--brand-text-muted)',
          backgroundColor: 'white',
        }}
        aria-label="Share vendor"
      >
        {copied ? (
          <>
            <Icon name="check" size={16} style={{ color: 'var(--color-success)' }} />
            <span style={{ color: 'var(--color-success)' }}>Copied!</span>
          </>
        ) : (
          <>
            <Icon name="share" size={16} />
            Share
          </>
        )}
      </button>

      {/* Fallback menu when clipboard API isn't available */}
      {showMenu && (
        <div
          className="absolute right-0 top-full z-10 mt-2 w-72 rounded-lg border bg-white p-3 shadow-lg"
          style={{ borderColor: 'var(--brand-card-border)' }}
        >
          <p
            className="mb-2 text-xs font-medium"
            style={{ color: 'var(--brand-input-placeholder)' }}
          >
            Share this vendor
          </p>
          <div className="flex items-center gap-2">
            <input
              type="text"
              readOnly
              value={shareUrl}
              className="flex-1 truncate rounded border px-2 py-1.5 text-xs"
              style={{
                borderColor: 'var(--brand-card-border)',
                color: 'var(--brand-text-muted)',
              }}
              onClick={(e) => (e.target as HTMLInputElement).select()}
            />
            <button
              onClick={handleCopyFromMenu}
              className="inline-flex items-center gap-1 rounded border px-2 py-1.5 text-xs font-medium transition-colors"
              style={{
                borderColor: 'var(--brand-card-border)',
                color: 'var(--brand-primary, #014F8A)',
              }}
            >
              {copied ? <Icon name="check" size={12} /> : <Icon name="content_copy" size={12} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
